import { buildPuzzle } from '@rendever/puzzle-core/crossword';
const VERIFIED = 'Built + verified with tools/crossword-build: fill from Spread the Word(list) ' +
    '(CC BY-NC-SA 4.0; puzzle sellable, list not redistributed) ∩ ENABLE (public ' +
    'domain); difficulty via wordfreq (Apache-2.0). Clues authored by hand.';
/* ========================================================================== *
 *  OPEN 5×5 WORD SQUARES — every row equals the same-numbered column, so the
 *  grid is trivially hand-verifiable. Fully clean fully-open squares are rare,
 *  so these are hand-picked; graded hard (the fill leans on a couple of less-
 *  everyday but real words). Exercises the renderer's all-white path.
 * ========================================================================== */
const MINI_OPEN_CRANE = buildPuzzle({
    id: 'mini-0001',
    size: 'mini',
    difficulty: 'hard',
    layout: ['CRANE', 'RAVEN', 'AVERT', 'NERVE', 'ENTER'],
    clues: {
        '1A': 'Tall bird that stands one-legged in the shallows',
        '6A': 'Glossy black bird that croaks "nevermore"',
        '7A': 'Head off, as disaster',
        '8A': 'What you steel before a bold move',
        '9A': 'Walk through the door',
        '1D': 'Long-armed machine on a building site',
        '2D': 'Greedy sort, or a bird of ill omen',
        '3D': 'Sidestep, as a question',
        '4D': 'Bravery, or cheek',
        '5D': 'Come in',
    },
    title: 'Word Square',
    attribution: VERIFIED,
});
const MINI_OPEN_OTHER = buildPuzzle({
    id: 'mini-0002',
    size: 'mini',
    difficulty: 'hard',
    layout: ['OTHER', 'FRAME', 'TOTES', 'EVENT', 'NERDS'],
    clues: {
        '1A': 'The remaining one of two',
        '6A': 'What a picture hangs in',
        '7A': 'Carries, in cosy talk',
        '8A': 'A wedding or a fair, say',
        '9A': 'Brainy sorts who love a fact',
        '1D': 'Many a time; frequently',
        '2D': 'A hidden hoard of treasure',
        '3D': 'One who just can’t stand it',
        '4D': 'Correct, as a typo in the text',
        '5D': 'Takes a breather, with "up"',
    },
    title: 'Word Square',
    attribution: VERIFIED,
});
/* ========================================================================== *
 *  5×5 WITH BLACK SQUARES — the everyday Mini rotation. Mix of templates
 *  (corner blocks, diagonal, staircase) so the renderer sees varied numbering.
 *  Graded easy/medium by how plain the words and clues are.
 * ========================================================================== */
const MINI_MUCH = buildPuzzle({
    id: 'mini-0003',
    size: 'mini',
    difficulty: 'easy',
    layout: ['MUCH#', 'APRON', 'SPARE', 'TENSE', '#REED'],
    clues: {
        '1A': 'A whole lot',
        '5A': 'You tie it on before cooking',
        '7A': 'The fifth wheel, or to set aside',
        '8A': 'Wound up tight, nerves and all',
        '9A': 'Tall grass at the water’s edge',
        '1D': 'Ship’s pole that holds the sails',
        '2D': 'Top floor, or higher up',
        '3D': 'Tall wading bird, or a building hoist',
        '4D': 'Animal you saddle up',
        '6D': 'Must have',
    },
    title: 'Mini',
    attribution: VERIFIED,
});
const MINI_EACH = buildPuzzle({
    id: 'mini-0004',
    size: 'mini',
    difficulty: 'medium',
    layout: ['EACH#', 'ALLOW', 'STORE', 'TENSE', '#REED'],
    clues: {
        '1A': 'Every single one',
        '5A': 'Permit; give the nod to',
        '7A': 'Shop, or to stock away',
        '8A': 'On edge, like a thriller’s last scene',
        '9A': 'Marsh plant cut for thatching',
        '1D': 'Toward the sunrise',
        '2D': 'Change to suit, as a hem',
        '3D': 'A copy made from one original',
        '4D': 'It pulls a carriage',
        '6D': 'Unwanted sprout in the garden',
    },
    title: 'Mini',
    attribution: VERIFIED,
});
const MINI_SHOW = buildPuzzle({
    id: 'mini-0005',
    size: 'mini',
    difficulty: 'medium',
    layout: ['SHOW#', 'TAXES', 'ABIDE', 'RIDGE', '#TEEN'],
    clues: {
        '1A': 'Put on display',
        '5A': 'April’s dreaded dues',
        '7A': 'Put up with; tolerate',
        '8A': 'The long top of a roof or a hill',
        '9A': 'Thirteen to nineteen years old',
        '1D': 'Twinkler in the night sky',
        '2D': 'Something you do without thinking',
        '3D': 'Rust is one, on iron',
        '4D': 'Doorstop shape, or a slice of pie',
        '6D': 'Spotted; laid eyes on',
    },
    title: 'Mini',
    attribution: VERIFIED,
});
const MINI_PART = buildPuzzle({
    id: 'mini-0006',
    size: 'mini',
    difficulty: 'medium',
    layout: ['PART#', 'OCEAN', 'PULSE', 'STATE', '#EYED'],
    clues: {
        '1A': 'A piece, not the whole',
        '5A': 'The Atlantic or the Pacific',
        '7A': 'The thump you feel at your wrist',
        '8A': 'Texas or Maine, for one',
        '9A': 'Looked at, with "up and down"',
        '1D': 'Music played from speakers, for short',
        '2D': 'Sharp, like a keen sense or an angle',
        '3D': 'Pass the baton in a team race',
        '4D': 'Flavor on the tongue',
        '6D': 'It’s a must',
    },
    title: 'Mini',
    attribution: VERIFIED,
});
const MINI_DOWN = buildPuzzle({
    id: 'mini-0007',
    size: 'mini',
    difficulty: 'medium',
    layout: ['DOWN#', 'ERROR', 'ABIDE', 'RITES', '#TEST'],
    clues: {
        '1A': 'The opposite of up',
        '5A': 'A slip or a blunder',
        '7A': 'Stick to, as the rules',
        '8A': 'Solemn ceremonies',
        '9A': 'An exam, or a trial run',
        '1D': 'Beloved, like an old friend',
        '2D': 'A moon’s path around a planet',
        '3D': 'Put pen to paper',
        '4D': 'Knots or points on a network',
        '6D': 'Take a breather',
    },
    title: 'Mini',
    attribution: VERIFIED,
});
const MINI_LEFT = buildPuzzle({
    id: 'mini-0008',
    size: 'mini',
    difficulty: 'medium',
    layout: ['LEFT#', 'AVOID', 'TERRA', 'ENTER', '#TEST'],
    clues: {
        '1A': 'Port side, at sea',
        '5A': 'Steer clear of',
        '7A': 'Latin for "earth," as in firma',
        '8A': 'Type onto the screen, or step inside',
        '9A': 'A pop quiz, say',
        '1D': 'Not early',
        '2D': 'A happening on the calendar',
        '3D': 'A strong point, or one’s specialty',
        '4D': 'They get bald and need changing',
        '6D': 'A quick throw at the bullseye',
    },
    title: 'Mini',
    attribution: VERIFIED,
});
const MINI_COOL = buildPuzzle({
    id: 'mini-0009',
    size: 'mini',
    difficulty: 'hard',
    layout: ['COOL#', 'ATLAS', 'RHINO', 'SEVER', '#REST'],
    clues: {
        '1A': 'Pleasantly chilly, or very stylish',
        '5A': 'A book of maps',
        '7A': 'Thick-skinned beast with a horn',
        '8A': 'Cut clean through',
        '9A': 'What you do after a long day',
        '1D': 'They roll on the road',
        '2D': 'Not this one, but the ___',
        '3D': 'Green fruit pressed for oil',
        '4D': 'Strips between the curbs',
        '6D': 'Put in order, as a list',
    },
    title: 'Mini',
    attribution: VERIFIED,
});
const MINI_LAST = buildPuzzle({
    id: 'mini-0010',
    size: 'mini',
    difficulty: 'hard',
    layout: ['LAST#', 'ITCHY', 'FLORA', 'TAPER', '#SEED'],
    clues: {
        '1A': 'Final; bringing up the rear',
        '5A': 'How a wool sweater can feel',
        '7A': 'The plant life of a region',
        '8A': 'A slim candle, or to narrow to a point',
        '9A': 'You plant it to grow a flower',
        '1D': 'Hoist, as a heavy box',
        '2D': 'The book of maps again',
        '3D': 'The reach of a telescope',
        '4D': 'A trio in number',
        '6D': 'A measure of cloth, or the back garden',
    },
    title: 'Mini',
    attribution: VERIFIED,
});
const MINI_ACT = buildPuzzle({
    id: 'mini-0011',
    size: 'mini',
    difficulty: 'easy',
    layout: ['##ACT', '#SPAR', 'APPLY', 'GILL#', 'ONE##'],
    clues: {
        '1A': 'A part of a play',
        '4A': 'Trade light blows, as boxers warming up',
        '5A': 'Put on, as cream — or hand in a form',
        '6A': 'A fish breathes through it',
        '7A': 'The loneliest number',
        '1D': 'Fruit a day keeps the doctor away',
        '2D': 'Ring up on the phone',
        '3D': 'Give it a ___ (make an attempt)',
        '4D': 'Make the wheel go round',
        '5D': 'In the past; "long ___"',
    },
    title: 'Mini',
    attribution: VERIFIED,
});
const MINI_BUT = buildPuzzle({
    id: 'mini-0012',
    size: 'mini',
    difficulty: 'easy',
    layout: ['##BUT', '#TONE', 'TRAIN', 'REST#', 'YET##'],
    clues: {
        '1A': 'On the other hand…',
        '4A': 'The mood of a voice, or a shade of color',
        '5A': 'The one with the engine and carriages',
        '6A': 'Take a load off',
        '7A': 'Up to now',
        '1D': 'Brag, or show off',
        '2D': 'A single piece, as on a shelf',
        '3D': 'One more than nine',
        '4D': 'It grows tall and gives shade',
        '5D': 'Give it a go',
    },
    title: 'Mini',
    attribution: VERIFIED,
});
const MINI_SEE = buildPuzzle({
    id: 'mini-0013',
    size: 'mini',
    difficulty: 'easy',
    layout: ['##SEE', '#SWAY', 'THOSE', 'WERE#', 'ODD##'],
    clues: {
        '1A': 'Take a look',
        '4A': 'Rock gently, like branches in a breeze',
        '5A': 'Not these, but ___',
        '6A': '"As you ___ saying…"',
        '7A': 'Peculiar; not even',
        '1D': 'A knight’s blade',
        '2D': 'Relax, or put at ___',
        '3D': 'You look with it',
        '4D': 'Cast off, as old skin',
        '5D': 'A pair, in number',
    },
    title: 'Mini',
    attribution: VERIFIED,
});
const MINI_WHO = buildPuzzle({
    id: 'mini-0014',
    size: 'mini',
    difficulty: 'easy',
    layout: ['##WHO', '#COAL', 'SOUND', 'HIND#', 'END##'],
    clues: {
        '1A': '"Guess ___ came to dinner"',
        '4A': 'Black lump that fires the furnace',
        '5A': 'Something you hear; also "safe and ___"',
        '6A': 'Toward the back, as a horse’s ___ legs',
        '7A': 'The finish',
        '1D': 'An injury, or to hurt',
        '2D': 'It fits in your palm and holds things',
        '3D': 'Not young; aged',
        '4D': 'A penny is one',
        '5D': 'A lady',
    },
    title: 'Mini',
    attribution: VERIFIED,
});
const MINI_ARE = buildPuzzle({
    id: 'mini-0015',
    size: 'mini',
    difficulty: 'easy',
    layout: ['##ARE', '#PLAN', 'SOLID', 'UPON#', 'NEW##'],
    clues: {
        '1A': '"Here we ___"',
        '4A': 'A scheme worked out ahead of time',
        '5A': 'Firm, like a rock — not liquid or gas',
        '6A': '"Once ___ a time…"',
        '7A': 'Just bought; brand ___',
        '1D': 'Let happen; give the go-ahead',
        '2D': 'It falls from the clouds',
        '3D': 'The very last bit',
        '4D': 'The head of the church in Rome',
        '5D': 'It rises in the morning',
    },
    title: 'Mini',
    attribution: VERIFIED,
});
const MINI_HER = buildPuzzle({
    id: 'mini-0016',
    size: 'mini',
    difficulty: 'easy',
    layout: ['##HER', '#COME', 'FLUID', 'EAST#', 'EYE##'],
    clues: {
        '1A': 'Belonging to that lady',
        '4A': 'Arrive; "___ on in!"',
        '5A': 'A liquid that flows',
        '6A': 'Where the sun comes up',
        '7A': 'You wink with it',
        '1D': 'Where you live',
        '2D': 'Let out, as steam',
        '3D': 'A stop light’s color',
        '4D': 'Soft earth you can shape and bake',
        '5D': 'A small charge, as for a service',
    },
    title: 'Mini',
    attribution: VERIFIED,
});
const MINI_SEEPLAY = buildPuzzle({
    id: 'mini-0017',
    size: 'mini',
    difficulty: 'medium',
    layout: ['##SEE', '#PLAY', 'BRACE', 'OATH#', 'BYE##'],
    clues: {
        '1A': 'Lay eyes on',
        '4A': 'What children do at recess',
        '5A': 'Steady yourself, or a pair',
        '6A': 'A solemn promise',
        '7A': 'A wave-off farewell',
        '1D': 'Gray rock split for roof tiles',
        '2D': 'Every single one',
        '3D': 'You look with it',
        '4D': 'Say a quiet word to the heavens',
        '5D': 'Float gently up and down on the water',
    },
    title: 'Mini',
    attribution: VERIFIED,
});
const MINI_FOR = buildPuzzle({
    id: 'mini-0018',
    size: 'mini',
    difficulty: 'easy',
    layout: ['##FOR', '#FINE', 'WORLD', 'ARMY#', 'YES##'],
    clues: {
        '1A': 'In favor of',
        '4A': 'Just dandy; doing well',
        '5A': 'The whole planet',
        '6A': 'Soldiers in their thousands',
        '7A': 'The opposite of no',
        '1D': 'Companies, in brief',
        '2D': 'Solely; the ___ one',
        '3D': 'A stop sign’s color',
        '4D': 'The front, as of a ship',
        '5D': 'The road traveled',
    },
    title: 'Mini',
    attribution: VERIFIED,
});
const MINI_SEESTAY = buildPuzzle({
    id: 'mini-0019',
    size: 'mini',
    difficulty: 'medium',
    layout: ['##SEE', '#STAY', 'HORSE', 'EMIT#', 'REP##'],
    clues: {
        '1A': 'Catch sight of',
        '4A': 'Remain put',
        '5A': 'It trots, gallops, and neighs',
        '6A': 'Give off, as light or heat',
        '7A': 'A salesperson, for short',
        '1D': 'A long thin piece, as of bacon or film',
        '2D': 'Toward the sunrise',
        '3D': 'You look with it',
        '4D': 'A handful; "___ folks"',
        '5D': 'The lady over there',
    },
    title: 'Mini',
    attribution: VERIFIED,
});
const MINI_TWO = buildPuzzle({
    id: 'mini-0020',
    size: 'mini',
    difficulty: 'medium',
    layout: ['##TWO', '#SHOW', 'STERN', 'HARD#', 'EYE##'],
    clues: {
        '1A': 'A pair’s count',
        '4A': 'A stage performance',
        '5A': 'Strict and no-nonsense; also a boat’s back end',
        '6A': 'Tough, or solid as rock',
        '7A': 'You see with it',
        '1D': 'In that spot, not here',
        '2D': 'A written message of a few letters',
        '3D': 'Belonging to oneself',
        '4D': 'Remain in place',
        '5D': 'The lady',
    },
    title: 'Mini',
    attribution: VERIFIED,
});
const MINI_WHOTHOU = buildPuzzle({
    id: 'mini-0021',
    size: 'mini',
    difficulty: 'medium',
    layout: ['##WHO', '#THOU', 'GUILT', 'ABLE#', 'SEE##'],
    clues: {
        '1A': 'The question word for a person',
        '4A': 'The old word for "you"',
        '5A': 'The heavy feeling after doing wrong',
        '6A': 'Up to the task; capable',
        '7A': 'Take a peek',
        '1D': 'For a ___ (for a stretch of time)',
        '2D': 'A doughnut’s middle',
        '3D': 'Not in; stepped ___',
        '4D': 'A hollow pipe',
        '5D': 'What you pump into a tire, for short',
    },
    title: 'Mini',
    attribution: VERIFIED,
});
const MINI_ASHES = buildPuzzle({
    id: 'mini-0022',
    size: 'mini',
    difficulty: 'medium',
    layout: ['BUT##', 'ASHES', 'NERVE', 'GREEN', '##END'],
    clues: {
        '1A': 'However; on the contrary',
        '4A': 'Gray powder left in the fireplace',
        '7A': 'Cheek, or the cord that carries a twinge',
        '8A': 'The color of spring grass',
        '9A': 'The very last part',
        '1D': 'A loud sudden noise',
        '2D': 'One who uses a tool or a website',
        '3D': 'A count just past two',
        '5D': 'Level, or on a par',
        '6D': 'Ship off in the mail',
    },
    title: 'Mini',
    attribution: VERIFIED,
});
const MINI_ADMIT = buildPuzzle({
    id: 'mini-0023',
    size: 'mini',
    difficulty: 'medium',
    layout: ['HIS##', 'ADMIT', 'LEASE', 'FALLS', '##LET'],
    clues: {
        '1A': 'Belonging to that fellow',
        '4A': 'Own up to; let in',
        '7A': 'A rental agreement',
        '8A': 'Tumbles down; also Niagara ___',
        '9A': 'Permit; rent out',
        '1D': 'Fifty cents of a dollar',
        '2D': 'A bright thought',
        '3D': 'Not big',
        '5D': 'A wee island',
        '6D': 'A trial of skill',
    },
    title: 'Mini',
    attribution: VERIFIED,
});
const MINI_CLOCK = buildPuzzle({
    id: 'mini-0024',
    size: 'mini',
    difficulty: 'medium',
    layout: ['ACT##', 'CLOCK', 'TITAN', 'SPARE', '##LEE'],
    clues: {
        '1A': 'Do something, or a play’s division',
        '4A': 'It ticks on the wall',
        '7A': 'A giant; a heavyweight of industry',
        '8A': 'The extra one kept in reserve',
        '9A': 'The sheltered side, away from the wind',
        '1D': 'Plays on a stage; "___ up" (gets going)',
        '2D': 'Snip with scissors, as a coupon',
        '3D': 'The whole sum',
        '5D': 'Look after with kindness',
        '6D': 'The bend in your leg',
    },
    title: 'Mini',
    attribution: VERIFIED,
});
const MINI_EVADE = buildPuzzle({
    id: 'mini-0025',
    size: 'mini',
    difficulty: 'hard',
    layout: ['HOW##', 'EVADE', 'LEGAL', 'PRESS', '##SHE'],
    clues: {
        '1A': 'By what means?',
        '4A': 'Dodge; slip away from',
        '7A': 'Allowed by the law',
        '8A': 'Push down on; also the newspapers',
        '9A': 'That lady',
        '1D': 'Lend a hand',
        '2D': 'Done; finished, as in "all ___"',
        '3D': 'The pay for a day’s work',
        '5D': 'A quick dart from one spot to another',
        '6D': 'Or ___ (if not)',
    },
    title: 'Mini',
    attribution: VERIFIED,
});
const MINI_ENACT = buildPuzzle({
    id: 'mini-0026',
    size: 'mini',
    difficulty: 'medium',
    layout: ['BIG##', 'ENACT', 'ACTOR', 'THERE', '##SEE'],
    clues: {
        '1A': 'Large in size',
        '4A': 'Pass into law; also, act out',
        '7A': 'One who plays a part on stage',
        '8A': 'In that place, not here',
        '9A': '"Wait and ___"',
        '1D': 'The steady thump of a drum',
        '2D': 'A foot, in number, has twelve of these',
        '3D': 'Doors and fences swing on them',
        '5D': 'The middle of an apple',
        '6D': 'It gives shade in the yard',
    },
    title: 'Mini',
    attribution: VERIFIED,
});
const MINI_REALM = buildPuzzle({
    id: 'mini-0027',
    size: 'mini',
    difficulty: 'hard',
    layout: ['ALL##', 'REALM', 'MANIA', 'SPEND', '##SEE'],
    clues: {
        '1A': 'Every bit of it',
        '4A': 'A king’s domain',
        '7A': 'A wild craze or obsession',
        '8A': 'Lay out money',
        '9A': 'Lay eyes on',
        '1D': 'They reach out from your shoulders',
        '2D': 'A jump forward',
        '3D': 'Strips of the road; bowling has them too',
        '5D': 'A drawn-out straight mark',
        '6D': 'Built, or had for breakfast',
    },
    title: 'Mini',
    attribution: VERIFIED,
});
const MINI_UNITE = buildPuzzle({
    id: 'mini-0028',
    size: 'mini',
    difficulty: 'medium',
    layout: ['OUT##', 'UNITE', 'TIDAL', 'STEMS', '##SEE'],
    clues: {
        '1A': 'Not at home',
        '4A': 'Join together as one',
        '7A': 'Of the sea’s rise and fall',
        '8A': 'The stalks that hold up flowers',
        '9A': 'Take a look',
        '1D': 'Three of these end a baseball inning',
        '2D': 'A single thing',
        '3D': 'The shore at high and low ___',
        '5D': 'Make calm, or housebreak',
        '6D': 'Or ___ (otherwise)',
    },
    title: 'Mini',
    attribution: VERIFIED,
});
const MINI_ROUTE = buildPuzzle({
    id: 'mini-0029',
    size: 'mini',
    difficulty: 'hard',
    layout: ['ACT##', 'ROUTE', 'MANIA', 'STEMS', '##SEE'],
    clues: {
        '1A': 'Take the stage',
        '4A': 'The path you take to get there',
        '7A': 'A frenzy; a runaway craze',
        '8A': 'The stalks of plants',
        '9A': 'Have a look',
        '1D': 'They swing at your sides as you walk',
        '2D': 'What you wear over a shirt and tie',
        '3D': 'Songs you can hum along to',
        '5D': 'O’clock; the hour',
        '6D': 'Put at rest; calm down',
    },
    title: 'Mini',
    attribution: VERIFIED,
});
const MINI_SCARY = buildPuzzle({
    id: 'mini-0030',
    size: 'mini',
    difficulty: 'medium',
    layout: ['DID##', 'UNITE', 'SCARY', 'THREE', '##YES'],
    clues: {
        '1A': 'Past tense of "do"',
        '4A': 'Bring together',
        '7A': 'Spooky, like a ghost story',
        '8A': 'The count after two',
        '9A': 'A nod of agreement',
        '1D': 'Fine grit blown off a shelf',
        '2D': 'Twelve of them make a foot',
        '3D': 'A daily journal of one’s doings',
        '5D': 'It grows tall and leafy',
        '6D': 'A pair of peepers',
    },
    title: 'Mini',
    attribution: VERIFIED,
});
const MINI_WHALE = buildPuzzle({
    id: 'mini-0031',
    size: 'mini',
    difficulty: 'medium',
    layout: ['LET##', 'ACRES', 'WHALE', 'NOISE', '##LED'],
    clues: {
        '1A': 'Allow; permit',
        '4A': 'Fields are measured in them',
        '7A': 'The biggest creature in the sea',
        '8A': 'A racket; a loud din',
        '9A': 'Guided; "she ___ the way"',
        '1D': 'A grassy patch by the road',
        '2D': 'A faint cry that bounces back',
        '3D': 'A winding mountain path',
        '5D': 'Or ___ (if not)',
        '6D': 'A small round dot a plant grows from',
    },
    title: 'Mini',
    attribution: VERIFIED,
});
const MINI_TASTE = buildPuzzle({
    id: 'mini-0032',
    size: 'mini',
    difficulty: 'hard',
    layout: ['SEE##', 'TASTE', 'ARSON', 'BLAND', '##YES'],
    clues: {
        '1A': 'Set eyes on',
        '4A': 'What the tongue does to soup',
        '7A': 'Setting a fire on purpose',
        '8A': 'Dull on the tongue; needs salt',
        '9A': 'Just so; agreed',
        '1D': 'A quick thrust, or a rough attempt',
        '2D': 'A count of the nobility',
        '3D': 'A short written composition',
        '5D': 'A musical pitch, or a phone’s hum',
        '6D': 'Where a story finishes',
    },
    title: 'Mini',
    attribution: VERIFIED,
});
const MINI_ARENA = buildPuzzle({
    id: 'mini-0033',
    size: 'mini',
    difficulty: 'hard',
    layout: ['FOR##', 'ARENA', 'MASON', 'ELITE', '##NEW'],
    clues: {
        '1A': 'On behalf of',
        '4A': 'The ring where the big match is held',
        '7A': 'A worker in stone and brick',
        '8A': 'The chosen few at the top',
        '9A': 'Fresh out of the box',
        '1D': 'Famous; "the hall of ___"',
        '2D': 'Spoken aloud, as an exam',
        '3D': 'Sticky sap from a pine tree',
        '5D': 'A short message to remember by',
        '6D': 'Once more; ___ and again',
    },
    title: 'Mini',
    attribution: VERIFIED,
});
const MINI_HINTS = buildPuzzle({
    id: 'mini-0034',
    size: 'mini',
    difficulty: 'medium',
    layout: ['THE##', 'HINTS', 'INTRO', 'STEEL', '##RED'],
    clues: {
        '1A': 'A little word before a noun',
        '4A': 'Small clues that nudge you along',
        '7A': 'The opening of a book, for short',
        '8A': 'The strong metal in girders and knives',
        '9A': 'The color of a ripe tomato',
        '1D': 'The one right here, near at hand',
        '2D': 'A small tip, or a suggestion',
        '3D': 'Go in through the door',
        '5D': 'A tall shady plant',
        '6D': 'Handed over; "she ___ the goods"',
    },
    title: 'Mini',
    attribution: VERIFIED,
});
const MINI_HANDS = buildPuzzle({
    id: 'mini-0035',
    size: 'mini',
    difficulty: 'medium',
    layout: ['TWO##', 'HANDS', 'ASSET', 'THEME', '##TOM'],
    clues: {
        '1A': 'A clock has this many pointing hands',
        '4A': 'They’re at the ends of your arms',
        '7A': 'A handy thing of value',
        '8A': 'The main idea running through a story',
        '9A': 'A male cat',
        '1D': '"___ one, please" (pointing it out)',
        '2D': 'Give a quick scrub',
        '3D': 'The start of something; "___ of war"',
        '5D': 'Show off a new product',
        '6D': 'A plant’s thin stalk',
    },
    title: 'Mini',
    attribution: VERIFIED,
});
const MINI_SOUNDPLAN = buildPuzzle({
    id: 'mini-0036',
    size: 'mini',
    difficulty: 'easy',
    layout: ['##SEE', '#STAY', 'WHERE', 'HORN#', 'OWN##'],
    clues: {
        '1A': 'Have a look',
        '4A': 'Don’t leave; ___ a while',
        '5A': 'At what place?',
        '6A': 'It honks on a car',
        '7A': 'Have as your very ___',
        '1D': 'Toward the back of a ship',
        '2D': 'Bring home, as a paycheck',
        '3D': 'You look with it',
        '4D': 'Put on a display',
        '5D': '"Says ___?"',
    },
    title: 'Mini',
    attribution: VERIFIED,
});
/* ========================================================================== *
 *  7×7 MIDIS — the honest hard tier (see header note). Hand-curated from the
 *  STW-fed constructor; every entry is a real, fairly-cluable dictionary word
 *  with no proper nouns or abbreviations, graded `hard`. A couple of advanced
 *  (but plainly cluable) words per grid are unavoidable in a clean 7×7 from any
 *  permissive vocabulary — they are clued straightforwardly.
 * ========================================================================== */
const MIDI_ELDERLY = buildPuzzle({
    id: 'midi-0001',
    size: 'midi',
    difficulty: 'hard',
    layout: ['TWO#POT', 'HER#AIR', 'ELDERLY', '#FEATS#', 'CARRIED', 'ARE#TEA', 'RED#ADD'],
    clues: {
        '1A': 'A pair, in number',
        '4A': 'A cooking vessel on the stove',
        '7A': 'Belonging to that lady',
        '8A': 'What you breathe',
        '9A': 'Up in years; getting on',
        '11A': 'Impressive deeds',
        '12A': 'Toted from here to there',
        '14A': '"Here we ___"',
        '15A': 'Afternoon cuppa',
        '16A': 'A stop light’s color',
        '17A': 'Sum up; tack on',
        '1D': 'A little word before "end" and "best"',
        '2D': 'Help for those in need; the safety net',
        '3D': 'Told what to do, or asked for at a restaurant',
        '4D': 'A musical suite in several movements',
        '5D': 'A plant grown for its pressed-out fat, like flax',
        '6D': 'Give it a go',
        '10D': 'You hear with it',
        '12D': 'It rolls down the road',
        '13D': 'Father, fondly',
    },
    title: 'Midi',
    attribution: VERIFIED,
});
const MIDI_SURPASS = buildPuzzle({
    id: 'midi-0002',
    size: 'midi',
    difficulty: 'hard',
    layout: ['ALL#AGE', 'SEA#GUN', 'STYMIED', '#TEATS#', 'SURPASS', 'ICE#TEA', 'RED#ODD'],
    clues: {
        '1A': 'Every last one',
        '4A': 'How old you are',
        '7A': 'The salty blue expanse',
        '8A': 'It fires with a bang',
        '9A': 'Blocked; left stuck with no way forward',
        '11A': 'A cow is milked by them',
        '12A': 'Go beyond; do better than',
        '14A': 'It chills your drink',
        '15A': 'Afternoon brew',
        '16A': 'A fire engine’s color',
        '17A': 'Strange; not even',
        '1D': 'A donkey',
        '2D': 'The crisp green in a salad',
        '3D': 'Stacked in even sheets, like a cake',
        '4D': 'Played in a stirred-up, lively way (musical direction)',
        '5D': 'Took a stab at the answer',
        '6D': 'The finish',
        '10D': 'A guide to find the way',
        '12D': 'A polite word for a gentleman',
        '13D': 'Down in the dumps',
    },
    title: 'Midi',
    attribution: VERIFIED,
});
const MIDI_LECTERN = buildPuzzle({
    id: 'midi-0003',
    size: 'midi',
    difficulty: 'hard',
    layout: ['ITS#TWO', 'LEE#HOW', 'LECTERN', '#NEARS#', 'GADGETS', 'AGE#TEA', 'YES#ODD'],
    clues: {
        '1A': 'Belonging to it',
        '4A': 'A pair’s count',
        '7A': 'The sheltered side, out of the wind',
        '8A': 'In what manner?',
        '9A': 'The stand a speaker reads from',
        '11A': 'Draws close to',
        '12A': 'Handy little devices and gizmos',
        '14A': 'A long span of time',
        '15A': 'Afternoon drink',
        '16A': 'The opposite of no',
        '17A': 'Peculiar; not even',
        '1D': 'Under the weather',
        '2D': 'Thirteen to nineteen years old',
        '3D': 'Pulls out of the union; breaks away',
        '4D': 'To that; "attached ___" (formal)',
        '5D': 'Defeated soundly; also a smooth wool yarn',
        '6D': 'Have as your very ___',
        '10D': 'Stick a label on, or a game of chase',
        '12D': 'Cheerful and bright; lighthearted',
        '13D': 'Gloomy; blue',
    },
    title: 'Midi',
    attribution: VERIFIED,
});
/**
 * The hand-authored corpus, in stable id order. The pipeline's generated
 * puzzles (if any) are concatenated in `index.ts`.
 */
export const HAND_AUTHORED = [
    MINI_OPEN_CRANE,
    MINI_OPEN_OTHER,
    MINI_MUCH,
    MINI_EACH,
    MINI_SHOW,
    MINI_PART,
    MINI_DOWN,
    MINI_LEFT,
    MINI_COOL,
    MINI_LAST,
    MINI_ACT,
    MINI_BUT,
    MINI_SEE,
    MINI_WHO,
    MINI_ARE,
    MINI_HER,
    MINI_SEEPLAY,
    MINI_FOR,
    MINI_SEESTAY,
    MINI_TWO,
    MINI_WHOTHOU,
    MINI_ASHES,
    MINI_ADMIT,
    MINI_CLOCK,
    MINI_EVADE,
    MINI_ENACT,
    MINI_REALM,
    MINI_UNITE,
    MINI_ROUTE,
    MINI_SCARY,
    MINI_WHALE,
    MINI_TASTE,
    MINI_ARENA,
    MINI_HINTS,
    MINI_HANDS,
    MINI_SOUNDPLAN,
    MIDI_ELDERLY,
    MIDI_SURPASS,
    MIDI_LECTERN,
];
//# sourceMappingURL=corpus.js.map